// @flow

import React, { PureComponent } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { push } from "connected-react-router/immutable";
import { createStructuredSelector } from "reselect";
import { createMuiTheme, MuiThemeProvider } from "@material-ui/core/styles";
import red from "@material-ui/core/colors/red";
import indigo from "@material-ui/core/colors/indigo";
import blueGrey from "@material-ui/core/colors/blueGrey";
import CssBaseline from "@material-ui/core/CssBaseline";
import ErrorBoundary from "containers/ErrorBoundary";
import { Redirect, Route, Switch } from "react-router-dom";
import SiteTshirtReport from "routes/private/Site/components/SiteTshirtReport";

import {
  closeDrawer,
  insertAfter,
  insertBefore,
  openDrawer,
  remove,
  replaceAll,
  reset,
  toggleDrawer,
} from "./duck";
import {
  hideMessage,
  showErrorMessage,
  showErrorMessageSimple,
  showMessage,
} from "containers/Snackbar/duck";
import { loadGlobals } from "common/globals/duck";
import { login, logout } from "containers/Security/duck";
import * as s from "./selectors";
import * as securitySelectors from "containers/Security/selectors";
import * as globalsSelectors from "common/globals/selectors";
import PrivateAppFrame from "components/PrivateAppFrame";
import NotLoggedIn from "./components/NotLoggedIn";
import { MuiPickersUtilsProvider } from "@material-ui/pickers";
import MomentUtils from "@date-io/moment";
// Code-splitted imports
import Loadable from "react-loadable";
import ICPCLoader from "components/ICPCLoader";
import type { PrivatePage } from "./types";

const Dashboard = Loadable({
  loader: () => import("routes/private/Dashboard"),
  loading: ICPCLoader,
});
const Documents = Loadable({
  loader: () => import("routes/private/Documents"),
  loading: ICPCLoader,
});
const TakeSurvey = Loadable({
  loader: () => import("routes/private/TakeSurvey"),
  loading: ICPCLoader,
});
const Team = Loadable({
  loader: () => import("routes/private/Team"),
  loading: ICPCLoader,
});
const TeamCertification = Loadable({
  loader: () => import("routes/private/TeamCertification"),
  loading: ICPCLoader,
});
const Myteams = Loadable({
  loader: () => import("routes/private/Myteams"),
  loading: ICPCLoader,
});
const TeamRegistration = Loadable({
  loader: () => import("routes/private/TeamRegistration"),
  loading: ICPCLoader,
});
const TeamsRegistered = Loadable({
  loader: () => import("routes/private/TeamsRegistered"),
  loading: ICPCLoader,
});
const Contests = Loadable({
  loader: () => import("routes/private/Contests"),
  loading: ICPCLoader,
});
const PersonRegistration = Loadable({
  loader: () => import("routes/private/PersonRegistration"),
  loading: ICPCLoader,
});
const Site = Loadable({
  loader: () => import("routes/private/Site"),
  loading: ICPCLoader,
});
const Institutions = Loadable({
  loader: () => import("routes/private/Institutions"),
  loading: ICPCLoader,
});
const Institution = Loadable({
  loader: () => import("routes/private/Institution"),
  loading: ICPCLoader,
});
const SuggestInstitution = Loadable({
  loader: () => import("routes/private/SuggestInstitution"),
  loading: ICPCLoader,
});
const MergeAccounts = Loadable({
  loader: () => import("routes/private/MergeAccounts"),
  loading: ICPCLoader,
});
const Countries = Loadable({
  loader: () => import("routes/private/Countries"),
  loading: ICPCLoader,
});
const Country = Loadable({
  loader: () => import("routes/private/Country"),
  loading: ICPCLoader,
});
const Teams = Loadable({
  loader: () => import("routes/private/Teams"),
  loading: ICPCLoader,
});
const People = Loadable({
  loader: () => import("routes/private/People"),
  loading: ICPCLoader,
});
const Search = Loadable({
  loader: () => import("routes/private/Search"),
  loading: ICPCLoader,
});
const Help = Loadable({
  loader: () => import("routes/private/Help"),
  loading: ICPCLoader,
});
const ContestMaterials = Loadable({
  loader: () => import("routes/private/ContestMaterials"),
  loading: ICPCLoader,
});
const TransportationSurvey = Loadable({
  loader: () => import("routes/private/TransportationSurvey"),
  loading: ICPCLoader,
});
const HotelReservation = Loadable({
  loader: () => import("routes/private/HotelReservation"),
  loading: ICPCLoader,
});
const Event = Loadable({
  loader: () => import("routes/private/Event"),
  loading: ICPCLoader,
});
const RegisteredEvent = Loadable({
  loader: () => import("routes/private/RegisteredEvent"),
  loading: ICPCLoader,
});
const ContestApp = Loadable({
  loader: () => import("routes/private/ContestApp"),
  loading: ICPCLoader,
});

const theme = createMuiTheme({
  overrides: {
    MuiTableCell: {
      root: {
        /*whiteSpace: "normal",
                wordWrap: "break-word"*/
        overflow: "hidden",
      },
    },
    MuiFormLabel: {
      root: {
        maxWidth: "100%",
      },
    },
    MuiModal: {
      root: {
        zIndex: 1600,
      },
    },
  },
  palette: {
    primary: {
      light: indigo[300],
      main: indigo[500],
      dark: indigo[700],
    },
    secondary: {
      light: blueGrey[300],
      main: blueGrey[500],
      dark: blueGrey[700],
    },
    error: {
      light: red[300],
      main: red[500],
      dark: red[700],
    },
  },
});

/**
 * PrivateAppContainer - Our root component
 * This is where everything global lives, e.g.:
 * 1. Messaging (snackbar)
 * 2. Authentication/Authorization
 * 3. Top/bottom menu controls
 * 4. ...
 *
 * Look into duck.js for action creators you can dispatch anywhere in the app
 */
class PrivateAppContainer extends PureComponent<PrivatePage> {
  componentDidMount() {
    this.props.loadGlobals();
  }

  render() {
    const {
      userRoles,
      closeDrawer,
      toggleDrawer,
      isDrawerOpen,
      isMobileDrawerOpen,
      itemsForAdmin,
      itemsForCanSeeContest,
      itemsForRegularUser,
      currentUser,
      isGlobalsLoading,
      logout,
      loggedIn,
      currentUserId,
      dispatch,
      isLoading,
    } = this.props;
    const items = userRoles.isAdmin()
      ? itemsForAdmin
      : userRoles.canSeeContest()
      ? itemsForCanSeeContest
      : itemsForRegularUser;
    const { children, match, ...other } = this.props;
    if (isLoading) {
      return <ICPCLoader />;
    }
    return (
      <MuiThemeProvider theme={theme}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <CssBaseline />
          <ErrorBoundary>
            {!loggedIn ? (
              <NotLoggedIn />
            ) : (
              <PrivateAppFrame
                closeDrawer={closeDrawer}
                isDrawerOpen={isDrawerOpen}
                isMobileDrawerOpen={isMobileDrawerOpen}
                items={items}
                currentUser={currentUser}
                canSeeContest={userRoles.canSeeContest()}
                isAdmin={userRoles.isAdmin()}
                pathname={this.props.location.pathname}
                redirect={this.props.redirect}
                onButtonClick={toggleDrawer}
                logout={logout}
                dispatch={dispatch}
                isLoading={isLoading}
              >
                {!isGlobalsLoading && (
                  <Switch>
                    <Route
                      path={`${match.url}/dashboard`}
                      exact
                      render={(route) => <Dashboard {...route} {...other} />}
                    />
                    <Route
                      path={`${match.url}/documents`}
                      exact
                      render={(route) => <Documents {...route} {...other} />}
                    />
                    <Route
                      path={`${match.url}/takeSurvey/:surveyId`}
                      exact
                      render={(route) => <TakeSurvey {...route} {...other} />}
                    />
                    <Route
                      path={`${match.url}/previewSurvey/:surveyId`}
                      exact
                      render={(route) => (
                        <TakeSurvey {...route} {...other} preview={true} />
                      )}
                    />
                    <Route
                      path={`${match.url}/countries`}
                      exact
                      render={(route) => <Countries {...route} {...other} />}
                    />
                    <Route
                      path={`${match.url}/countries/:countryA2`}
                      exact
                      render={(route) => <Country {...route} {...other} />}
                    />
                    <Route
                      path={`${match.url}/myteams`}
                      exact
                      render={(route) => <Myteams {...route} {...other} />}
                    />
                    <Route
                      path={`${match.url}/teamRegistration/site/:siteId`}
                      exact
                      render={(route) => (
                        <TeamRegistration
                          {...route}
                          {...other}
                          bookmarkedSiteId={route.match.params.siteId}
                        />
                      )}
                    />
                    <Route
                      path={`${match.url}/teamRegistration`}
                      exact
                      render={(route) => (
                        <TeamRegistration {...route} {...other} />
                      )}
                    />
                    <Route
                      path={`${match.url}/teams/:teamId`}
                      exact
                      render={(route) => <Team {...route} {...other} />}
                    />
                    <Route
                      path={`${match.url}/teams/:teamId/certification`}
                      exact
                      render={(route) => (
                        <TeamCertification {...route} {...other} />
                      )}
                    />
                    ]
                    <Route
                      path={`${match.url}/teamsRegistered`}
                      exact
                      render={(route) => (
                        <TeamsRegistered {...route} {...other} />
                      )}
                    />
                    <Route
                      path={`${match.url}/teams`}
                      exact
                      render={(route) => <Teams {...route} {...other} />}
                    />
                    <Route
                      path={`${match.url}/contests`}
                      exact
                      render={(route) => <Contests {...route} {...other} />}
                    />
                    <Route
                      path={`${match.url}/contest/:contestId`}
                      render={(route) => <ContestApp {...route} {...other} />}
                    />
                    <Route
                      path={`${match.url}/(profile|person)/:personId`}
                      render={(route) => (
                        <PersonRegistration {...route} {...other} />
                      )}
                    />
                    <Route
                      path={`${match.url}/site/:siteId/tshirt`}
                      exact
                      render={(route) => (
                        <SiteTshirtReport {...route} {...other} />
                      )}
                    />
                    <Route
                      path={`${match.url}/site/:siteId`}
                      render={(route) => <Site {...route} {...other} />}
                    />
                    <Route
                      path={`${match.url}/persons`}
                      exact
                      render={(route) => <People {...route} {...other} />}
                    />
                    <Route
                      path={`${match.url}/institutions`}
                      exact
                      render={(route) => <Institutions {...route} {...other} />}
                    />
                    <Route
                      path={`${match.url}/institutions/suggest`}
                      exact
                      render={(route) => (
                        <SuggestInstitution {...route} {...other} />
                      )}
                    />
                    {
                      <Route
                        path={`${match.url}/mergeAccounts`}
                        exact
                        render={(route) => (
                          <MergeAccounts {...route} {...other} />
                        )}
                      />
                    }
                    <Route
                      path={`${match.url}/institutions/:institutionId`}
                      exact
                      render={(route) => <Institution {...route} {...other} />}
                    />
                    <Route
                      path={`${match.url}/register`}
                      exact
                      render={(route) => <Register {...route} {...other} />}
                    />
                    <Route
                      path={`${match.url}/search`}
                      exact
                      render={(route) => <Search {...route} {...other} />}
                    />
                    <Route
                      path={match.url}
                      exact
                      render={(route) => <Dashboard {...route} {...other} />}
                    />
                    <Route
                      path={`${match.url}/help`}
                      render={(route) => <Help {...route} {...other} />}
                    />
                    <Route
                      path={`${match.url}/contestMaterials`}
                      render={(route) => (
                        <ContestMaterials {...route} {...other} />
                      )}
                    />
                    <Route
                      path={`${match.url}/transportation/:contestId`}
                      render={(route) => (
                        <TransportationSurvey {...route} {...other} />
                      )}
                    />
                    <Route
                      path={`${match.url}/hotelReservation/:contestId`}
                      render={(route) => (
                        <HotelReservation {...route} {...other} />
                      )}
                    />
                    <Route
                      path={`${match.url}/events/:id`}
                      exact
                      render={(route) => <Event {...route} {...other} />}
                    />
                    <Route
                      path={`${match.url}/eventRegistered/:id`}
                      exact
                      render={(route) => (
                        <RegisteredEvent {...route} {...other} />
                      )}
                    />
                    <Redirect
                      from={`${match.url}/profile`}
                      to={`${match.url}/profile/${currentUserId}`}
                    />
                  </Switch>
                )}
              </PrivateAppFrame>
            )}
          </ErrorBoundary>
        </MuiPickersUtilsProvider>
      </MuiThemeProvider>
    );
  }
}

const mapStateToProps = (state: Object, props: Object) =>
  createStructuredSelector({
    currentUser: securitySelectors.getCurrentUser,
    userRoles: securitySelectors.getUserRoles,
    currentUserId: securitySelectors.getUid,
    currentUserName: securitySelectors.getEmail,
    currentFirstName: securitySelectors.getFirstName,
    currentLastName: securitySelectors.getLastName,
    currentFullName: securitySelectors.getFullName,
    loggedIn: securitySelectors.isLoggedIn,
    isDrawerOpen: s.isDrawerOpen,
    isMobileDrawerOpen: s.isMobileDrawerOpen,
    itemsForAdmin: s.getForAdmin,
    itemsForCanSeeContest: s.getForCanSeeContest,
    itemsForRegularUser: s.getForRegularUser,
    worldFinalsYear: globalsSelectors.getWorldFinalsYear,
    regionalsYear: globalsSelectors.getRegionalsYear,
    year: globalsSelectors.getYear,
    selectedYear: globalsSelectors.getSelectedYear,
    worldFinalsContestId: globalsSelectors.getWorldFinalsContestId,
    isGlobalsLoading: globalsSelectors.isLoading,
    isGlobalsError: globalsSelectors.isError,
    videoStatus: globalsSelectors.getVideoStatus,
    isLoading: securitySelectors.isLoading,
  });

export function mapDispatchToProps(dispatch: any) {
  return {
    dispatch,
    ...bindActionCreators(
      {
        logout: logout,
        redirect: push,
        openDrawer,
        closeDrawer,
        toggleDrawer,
        loadGlobals,
        showMessage,
        showErrorMessage,
        showErrorMessageSimple,
        hideMessage,
        contextMenuInsertBefore: insertBefore,
        contextMenuInsertAfter: insertAfter,
        contextMenuRemoveItem: remove,
        contextMenuReplaceAll: replaceAll,
        contextMenuReset: reset,
      },
      dispatch
    ),
  };
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PrivateAppContainer);
